import React from 'react';
import withMainLayout from 'layouts/main';
import dynamic from 'next/dynamic';
import {Grid, Row, Col} from '@/grid';
import Container from '@/container';
import {SectionPadding, SectionPaddingHalfTop, SectionPaddingHalfBottom} from '@/section';
import {H1, H2, P, PMarginSm} from '@/typography';
import {BreakVisibleXxl} from '@/breaks';
import {withGreen800, withBgNeutral200Rounded} from '@/hocs/withColors';
import {withMarginBottomZeroToMd} from '@/hocs/withSpacing';
import PictureWrapper from '@/ui/images-wrapper';
import {ListOlNumbered} from '@/ui/lists/list-icons';
import {GridLayout, GridItem} from '@/ui/grid/grid-layout';
import BoxGrid from '@/ui/box-grid';

const H1Green800 = withGreen800(H1);
const DivBgNeutral200Rounded = withBgNeutral200Rounded('div');
const DivMarginedMobile = withMarginBottomZeroToMd('div');
const imgPath = '/static/images/developers/assessment/';

const SubmitPartnerApplication = dynamic(
  import('@/content/developers/submit-partner-application'),
  {
    loading: () => null
  }
);

const seoMetas = {
  title: 'Assessment tool integration | Workable developer partners',
  description:
    'Integrate your assessment tool with Workable. Help your customers set up candidate skills tests directly through their Workable recruiting pipeline.',
  canonical: 'https://www.workable.com/developers/partner-program/assessment'
};

const AssessmentPartnerPage = props => (
  <>
    <SectionPaddingHalfBottom>
      <Container variant="section-padding-below-nav">
        <SectionPaddingHalfTop>
          <Grid fluid>
            <Row middle="lg">
              <Col xs={12} lg={6}>
                <H1Green800 className="heading-1-smaller">
                  Connect your assessment
                  <BreakVisibleXxl /> tool with Workable
                </H1Green800>
                <PMarginSm>
                  Assessment tests help companies quickly identify the best candidates. Make your
                  tests available to Workable users for a more complete hiring process.
                </PMarginSm>
                <PMarginSm>
                  Companies use Workable to move candidates through their hiring pipeline - from
                  application to job offer. Before interviews, many want to test candidates' skills
                  to focus only on the most qualified people.
                </PMarginSm>
                <PMarginSm>
                  Our connections with assessment providers let Workable users set up tests quickly
                  as part of their regular hiring steps.
                </PMarginSm>
              </Col>
              <Col xs={12} lg={6}>
                <PictureWrapper
                  imgPath={imgPath}
                  imageUrl="assessment-hero"
                  imageTypeFallback="jpg"
                  alt="Connect your assessment tool with Workable - image"
                  fetchpriority="high"
                  loading="eager"
                  width={584}
                  height={624}
                />
              </Col>
            </Row>
          </Grid>
        </SectionPaddingHalfTop>
      </Container>
    </SectionPaddingHalfBottom>

    <SectionPadding>
      <Container>
        <Grid fluid>
          <Row middle="lg">
            <Col xs={12} lg={7}>
              <GridItem>
                <PictureWrapper
                  imgPath={imgPath}
                  imageUrl="connect-assessments"
                  imageTypeFallback="jpg"
                  alt="How customers connect Workable with assessment tools - image"
                />
              </GridItem>
            </Col>
            <Col xs={12} lg={5} first="xs" last="lg">
              <H2 className="heading-3">How customers connect Workable with assessment tools</H2>
              <PMarginSm>It's easy for Workable users to activate your integration:</PMarginSm>
              <DivMarginedMobile>
                <DivBgNeutral200Rounded>
                  <ListOlNumbered>
                    <li>They find it in the integrations list inside Workable</li>
                    <li>Existing users of both services follow simple on-screen instructions</li>
                    <li>New users see your logo, a description, and a link to learn more</li>
                  </ListOlNumbered>
                </DivBgNeutral200Rounded>
              </DivMarginedMobile>
            </Col>
          </Row>
        </Grid>
      </Container>
    </SectionPadding>

    <SectionPadding>
      <Container>
        <Grid fluid>
          <Row middle="lg">
            <Col xs={12} lg={6}>
              <H2 className="heading-3">Sending an assessment through Workable</H2>
              <PMarginSm>
                After connecting, a Workable admin or hiring manager can add your assessment when
                setting up a job:
              </PMarginSm>
              <DivBgNeutral200Rounded>
                <ListOlNumbered>
                  <li>Pick the right stage in the hiring process</li>
                  <li>Choose your test/assessment</li>
                  <li>Save changes</li>
                </ListOlNumbered>
              </DivBgNeutral200Rounded>
            </Col>
            <Col xs={12} lg={6}>
              <GridItem>
                <PictureWrapper
                  imgPath={imgPath}
                  imageUrl="sending-assessment"
                  imageTypeFallback="jpg"
                  alt="Sending an assessment through Workable - image"
                />
              </GridItem>
            </Col>
          </Row>
        </Grid>
      </Container>
    </SectionPadding>

    <SectionPadding>
      <Container>
        <Grid fluid>
          <Row middle="lg">
            <Col xs={12} lg={7}>
              <GridItem>
                <PictureWrapper
                  imgPath={imgPath}
                  imageUrl="connect-video-interviews-candidate"
                  imageTypeFallback="jpg"
                  alt="How customers connect Workable with assessment tools - image"
                />
              </GridItem>
            </Col>
            <Col xs={12} lg={5} first="xs" last="lg">
              <PMarginSm>
                When candidates reach that stage, sending the assessment takes just one click,
                without leaving Workable. Your assessment becomes a natural part of their hiring
                process.
              </PMarginSm>
            </Col>
          </Row>
        </Grid>
      </Container>
    </SectionPadding>

    <SectionPadding>
      <Container>
        <Grid fluid>
          <Row middle="lg">
            <Col xs={12} lg={5}>
              <PMarginSm>
                When results come back, they appear on the candidate's timeline in Workable. Users
                can also download a full report or see results on your own site.
              </PMarginSm>
            </Col>
            <Col xs={12} lg={6} lgOffset={1}>
              <GridLayout Col={1} autoRows>
                <GridItem>
                  <BoxGrid
                    backgroundColor="var(--brown50)"
                    Url="https://workable.readme.io/"
                    UrlCopy="View API documentation"
                    target="_blank"
                    minHeight={288}>
                    <PMarginSm className="heading-3">API docs</PMarginSm>
                    <P>
                      See how you could start to integrate your product or service with Workable
                    </P>
                  </BoxGrid>
                </GridItem>
              </GridLayout>
            </Col>
          </Row>
        </Grid>
      </Container>
    </SectionPadding>

    <SubmitPartnerApplication />
  </>
);

export default withMainLayout(AssessmentPartnerPage, {...seoMetas});

export {getSharedPageStaticProps as getStaticProps} from 'helpers/sharedPageStaticProps';
